import React from "/vendor/react";
import PropTypes from "prop-types";
import { makeStyles } from "/vendor/@material-ui/core";

import { CodeBlock } from "/lib/component/base";

const OVERLAY_MS = 1200;

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    cursor: "copy",
    "&:focus": {
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  },
  code: {
    margin: 0,
    "& code": {
      color: "#00ff41",
    },
  },
  overlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    color: "#00ff41",
    fontWeight: 500,
    fontSize: "1rem",
    pointerEvents: "none",
    opacity: 0,
    transition: "opacity 150ms ease-in-out",
  },
  overlayVisible: {
    opacity: 1,
  },
}));

const fallbackCopy = (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    if (!document.execCommand("copy")) {
      throw new Error("copy command was rejected");
    }
  } finally {
    document.body.removeChild(textarea);
  }
};

const copyToClipboard = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve) => resolve(fallbackCopy(text)));
};

const CheckDetailsCommand = ({ command }) => {
  const classes = useStyles();
  const [copied, setCopied] = React.useState(false);
  const timerRef = React.useRef(null);

  React.useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const copy = React.useCallback(() => {
    // Dragging to select part of the command should not overwrite the
    // clipboard with the whole command.
    const selection = window.getSelection && window.getSelection().toString();
    if (selection) {
      return;
    }

    copyToClipboard(command).then(
      () => {
        setCopied(true);
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => setCopied(false), OVERLAY_MS);
      },
      () => null,
    );
  }, [command]);

  const onKeyDown = React.useCallback(
    (ev) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        copy();
      }
    },
    [copy],
  );

  return (
    <div
      className={classes.root}
      role="button"
      tabIndex={0}
      title="Click to copy"
      onClick={copy}
      onKeyDown={onKeyDown}
    >
      <CodeBlock className={classes.code}>{command}</CodeBlock>
      <div
        className={`${classes.overlay} ${copied ? classes.overlayVisible : ""}`}
        aria-hidden={!copied}
      >
        Skopiowano!
      </div>
    </div>
  );
};

CheckDetailsCommand.propTypes = {
  command: PropTypes.string.isRequired,
};

export default CheckDetailsCommand;
